import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButton, ToggleButtonGroup, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from '../components/PageHeader';
import { offersApi, getApiErrorMessage } from '../services/api';
import { SpecialOfferTemplate, SpecialOfferTemplateInput, SpecialOfferType } from '../types';
import { NAME_PLACEHOLDER, fillRecipientName } from '../constants/specialOfferTemplates';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

/**
 * 特別オファー / 招待状 テンプレート設定画面（店舗別化）。
 * 課題126では固定FEプリセットだったテンプレを、店舗毎に登録・編集できるようにする。
 * - 全店共通テンプレ（is_common=true）は「共通」バッジ付きで読み取り専用。
 * - 店舗別テンプレは新規作成・編集・削除（ソフトデリート）が可能。
 * - {{name}} は送信時にバックエンドが宛先クルー名へ置換するため、テンプレには生のまま保持する。
 */
const OFFER_TYPE_LABEL: Record<SpecialOfferType, string> = {
  special: '特別オファー',
  invitation: '招待状',
};

const PREVIEW_NAME = 'お名前';

// 編集/新規フォームの内部状態
interface FormState {
  name: string;
  title_template: string;
  message_template: string;
  reward_template: string;
}

const EMPTY_FORM: FormState = { name: '', title_template: '', message_template: '', reward_template: '' };

const SpecialOfferTemplateSettings: React.FC = () => {
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<SpecialOfferTemplate[]>([]);
  const [offerType, setOfferType] = useState<SpecialOfferType>('special');
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  // 作成/編集ダイアログ
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SpecialOfferTemplate | null>(null); // null=新規
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [busy, setBusy] = useState(false);

  const notify = (msg: string, sev: 'success' | 'error') => setSnack({ msg, sev });

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const rows = await offersApi.listTemplates(storeId, token);
      setTemplates(rows);
    } catch (e) {
      notify(getApiErrorMessage(e, 'テンプレートの読み込みに失敗しました'), 'error');
    } finally {
      setLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(
    () => templates.filter((t) => t.offer_type === offerType),
    [templates, offerType],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (t: SpecialOfferTemplate) => {
    setEditing(t);
    setForm({
      name: t.name,
      title_template: t.title_template,
      message_template: t.message_template,
      reward_template: t.reward_template ?? '',
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const insertName = () => {
    setForm((prev) => ({ ...prev, message_template: `${prev.message_template}${NAME_PLACEHOLDER}` }));
  };

  const validate = (): boolean => {
    const errors: { [k: string]: string } = {};
    if (!form.name.trim()) errors.name = 'テンプレート名を入力してください';
    if (!form.title_template.trim()) errors.title_template = 'タイトルを入力してください';
    if (!form.message_template.trim()) errors.message_template = 'メッセージを入力してください';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload: SpecialOfferTemplateInput = {
        offer_type: offerType,
        name: form.name.trim(),
        title_template: form.title_template.trim(),
        message_template: form.message_template.trim(),
        reward_template: form.reward_template.trim() || null,
      };
      if (editing) {
        await offersApi.updateTemplate(storeId, editing.template_id, payload, token);
        notify('テンプレートを更新しました', 'success');
      } else {
        await offersApi.createTemplate(storeId, payload, token);
        notify('テンプレートを作成しました', 'success');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      notify(getApiErrorMessage(e, '保存に失敗しました'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: SpecialOfferTemplate) => {
    if (!window.confirm(`テンプレート「${t.name}」を削除しますか？`)) return;
    setBusy(true);
    try {
      await offersApi.deleteTemplate(storeId, t.template_id, token);
      notify('テンプレートを削除しました', 'success');
      load();
    } catch (e) {
      notify(getApiErrorMessage(e, '削除に失敗しました'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 2, maxWidth: 880, mx: 'auto' }}>
      <PageHeader title="特別オファーテンプレ" icon={<LocalOfferIcon />} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        特別オファー / 招待状の文面テンプレートを店舗ごとに登録できます。
        「共通」バッジ付きは全店共通テンプレートで、この画面からは編集できません。
        文中の「{NAME_PLACEHOLDER}」は送信時に宛先クルーの名前へ自動で置き換わります。
      </Typography>

      {/* 種別切替 */}
      <ToggleButtonGroup
        value={offerType}
        exclusive
        onChange={(_e, v) => { if (v) setOfferType(v as SpecialOfferType); }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="special">
          <LocalOfferIcon sx={{ fontSize: 18, mr: 0.75 }} />特別オファー
        </ToggleButton>
        <ToggleButton value="invitation">
          <MarkEmailUnreadIcon sx={{ fontSize: 18, mr: 0.75 }} />招待状
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={busy}>
          新しいテンプレートを追加
        </Button>
      </Box>

      <Stack spacing={1.5}>
        {shown.length === 0 && (
          <Paper sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid', borderColor: 'divider', p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              この種別のテンプレートはまだありません。「新しいテンプレートを追加」から登録してください。
            </Typography>
          </Paper>
        )}
        {shown.map((t) => (
          <Paper
            key={t.template_id}
            sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid', borderColor: 'divider', p: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.name}</Typography>
              {t.is_common ? (
                <Chip label="共通" size="small" color="default" variant="outlined" />
              ) : (
                <Chip label="店舗" size="small" color="primary" variant="outlined" />
              )}
              <Box sx={{ ml: 'auto' }}>
                {!t.is_common && (
                  <>
                    <IconButton size="small" aria-label="編集" onClick={() => openEdit(t)} disabled={busy}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" aria-label="削除" onClick={() => remove(t)} disabled={busy}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{fillRecipientName(t.title_template, PREVIEW_NAME)}</Typography>
            <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', color: 'text.secondary', mt: 0.5 }}>
              {fillRecipientName(t.message_template, PREVIEW_NAME)}
            </Typography>
            {t.reward_template && t.reward_template.trim() && (
              <Typography sx={{ fontSize: '0.8rem', color: '#E84024', mt: 0.5 }}>
                特典: {fillRecipientName(t.reward_template, PREVIEW_NAME)}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>

      {/* 作成 / 編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={busy ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'テンプレートを編集' : `新しい${OFFER_TYPE_LABEL[offerType]}テンプレート`}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="テンプレート名"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth required margin="normal"
            placeholder="例: 特別時給アップ"
            error={!!formErrors.name}
            helperText={formErrors.name || '一覧に表示される見出しです'}
            disabled={busy}
          />
          <TextField
            label="タイトル"
            value={form.title_template}
            onChange={(e) => setForm((p) => ({ ...p, title_template: e.target.value }))}
            fullWidth required margin="normal"
            placeholder="例: {{name}}さんへ特別時給のご案内"
            error={!!formErrors.title_template}
            helperText={formErrors.title_template || ''}
            disabled={busy}
          />
          <TextField
            label="メッセージ"
            value={form.message_template}
            onChange={(e) => setForm((p) => ({ ...p, message_template: e.target.value }))}
            fullWidth required multiline rows={5} margin="normal"
            placeholder="クルーへ伝えたいメッセージを入力してください。"
            error={!!formErrors.message_template}
            helperText={formErrors.message_template || ''}
            disabled={busy}
          />
          <Box sx={{ mt: 0.5 }}>
            <Chip label="お名前を挿入" size="small" variant="outlined" onClick={busy ? undefined : insertName} sx={{ cursor: 'pointer' }} />
            <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>
              「{NAME_PLACEHOLDER}」は送信時に宛先クルー名へ置換されます
            </Typography>
          </Box>
          <TextField
            label="報酬・特典（任意）"
            value={form.reward_template}
            onChange={(e) => setForm((p) => ({ ...p, reward_template: e.target.value }))}
            fullWidth margin="normal"
            placeholder="例: 時給+200円"
            disabled={busy}
          />

          {(form.title_template.trim() || form.message_template.trim()) && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f7f7f7', borderRadius: '10px' }}>
              <Typography variant="caption" sx={{ color: '#777', fontWeight: 700 }}>プレビュー</Typography>
              {form.title_template.trim() && (
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mt: 0.5 }}>
                  {fillRecipientName(form.title_template, PREVIEW_NAME)}
                </Typography>
              )}
              {form.message_template.trim() && (
                <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {fillRecipientName(form.message_template, PREVIEW_NAME)}
                </Typography>
              )}
              {form.reward_template.trim() && (
                <Typography sx={{ fontSize: '0.8rem', color: '#E84024', mt: 0.5 }}>
                  特典: {fillRecipientName(form.reward_template, PREVIEW_NAME)}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>キャンセル</Button>
          <Button onClick={save} variant="contained" disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : undefined}>
            {editing ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default SpecialOfferTemplateSettings;
